import { CURRENCY } from '../../config/currency';
interface OrderItem {
  name: string;
  qty: number;
  price: number;
}

interface Order {
  id: string;
  customOrderId?: string;
  date: string;
  status: string;
  total: number;
  vendor: string;
  items: OrderItem[];
  // Tax breakdown fields
  subtotal?: number;
  cgstRate?: number;
  cgstAmount?: number;
  sgstRate?: number;
  sgstAmount?: number;
  igstRate?: number;
  igstAmount?: number;
  serviceChargeRate?: number;
  serviceChargeAmount?: number;
  discountAmount?: number;
  taxType?: string;
}

interface OrderInvoiceProps {
  order: Order;
}

export default function OrderInvoice({ order }: OrderInvoiceProps) {
  const handleDownloadPDF = async () => {
    try {
      // Import jsPDF dynamically to avoid bundle size issues
      const { default: jsPDF } = await import('jspdf');
      
      // Create new PDF document
      const doc = new jsPDF();
      
      // Set font
      doc.setFont('helvetica');
      
      // Add title
      doc.setFontSize(20);
      doc.text('INVOICE', 20, 30);
      
      // Add order details
      doc.setFontSize(12);
      doc.text(`Vendor: ${order.vendor}`, 20, 50);
      doc.text(`Order ID: #${order.customOrderId || order.id}`, 20, 60);
      doc.text(`Date: ${order.date}`, 20, 70);
      doc.text(`Status: ${order.status}`, 20, 80);
      
      // Add items table
      doc.setFontSize(14);
      doc.text('ITEMS:', 20, 100);
      
      // Table headers
      doc.setFontSize(10);
      doc.text('Item', 20, 110);
      doc.text('Qty', 100, 110);
      doc.text('Price', 150, 110);
      
      // Add items
      let yPosition = 120;
      order.items.forEach((item, index) => {
        doc.text(item.name, 20, yPosition);
        doc.text(item.qty.toString(), 100, yPosition);
        doc.text(`Rs. ${item.price.toFixed(2)}`, 150, yPosition);
        yPosition += 10;
      });

      yPosition += 10;

      // Add tax breakdown if available
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      if (order.subtotal !== undefined && order.subtotal !== null) {
        doc.text(`Subtotal: Rs. ${order.subtotal.toFixed(2)}`, 20, yPosition);
        yPosition += 8;
      }

      // Discount before taxes/service charge — they are computed on the
      // discounted base, so display order mirrors calculation order
      if (order.discountAmount && order.discountAmount > 0) {
        doc.text(`Discount: -Rs. ${order.discountAmount.toFixed(2)}`, 20, yPosition);
        yPosition += 8;
      }

      if (order.cgstAmount && order.cgstAmount > 0) {
        doc.text(`CGST (${order.cgstRate}%): Rs. ${order.cgstAmount.toFixed(2)}`, 20, yPosition);
        yPosition += 8;
      }

      if (order.sgstAmount && order.sgstAmount > 0) {
        doc.text(`SGST (${order.sgstRate}%): Rs. ${order.sgstAmount.toFixed(2)}`, 20, yPosition);
        yPosition += 8;
      }

      if (order.igstAmount && order.igstAmount > 0) {
        doc.text(`IGST (${order.igstRate}%): Rs. ${order.igstAmount.toFixed(2)}`, 20, yPosition);
        yPosition += 8;
      }

      if (order.serviceChargeAmount && order.serviceChargeAmount > 0) {
        doc.text(`Service Charge (${order.serviceChargeRate}%): Rs. ${order.serviceChargeAmount.toFixed(2)}`, 20, yPosition);
        yPosition += 8;
      }

      yPosition += 5;

      // Add total
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`TOTAL: Rs. ${order.total.toFixed(2)}`, 20, yPosition);
      
      // Add footer
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Thank you for your business!', 20, doc.internal.pageSize.height - 20);
      
      // Save the PDF
      doc.save(`invoice-${order.customOrderId || order.id}.pdf`);
      
      console.log('PDF invoice downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className="bg-white border rounded-xl p-6 shadow-sm">
      <h3 className="text-lg font-semibold mb-4">Invoice</h3>

      <div className="text-sm mb-6 text-gray-700 space-y-1">
        <p><strong>Vendor:</strong> {order.vendor}</p>
        <p><strong>Order ID:</strong> #{order.customOrderId || order.id}</p>
        <p><strong>Date:</strong> {order.date}</p>
        <p><strong>Status:</strong> {order.status}</p>
      </div>

      <table className="w-full text-sm border-t border-b">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-3">Item</th>
            <th className="py-3">Qty</th>
            <th className="py-3 text-right">Price</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((item, idx) => (
            <tr key={idx} className="border-b border-gray-100">
              <td className="py-3">{item.name}</td>
              <td className="py-3">{item.qty}</td>
              <td className="py-3 text-right">{CURRENCY.symbol}{item.price.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Tax Breakdown Section */}
      <div className="mt-6 space-y-2 text-sm">
        {/* Subtotal */}
        {order.subtotal !== undefined && order.subtotal !== null && (
          <div className="flex justify-between">
            <span className="text-gray-500">Subtotal</span>
            <span className="text-gray-800">{CURRENCY.symbol}{order.subtotal.toFixed(2)}</span>
          </div>
        )}

        {/* Discount — before taxes/service charge: they are computed on the
            discounted base, so display order mirrors calculation order */}
        {order.discountAmount !== undefined && order.discountAmount !== null && order.discountAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Discount</span>
            <span className="text-green-600">-{CURRENCY.symbol}{order.discountAmount.toFixed(2)}</span>
          </div>
        )}

        {/* CGST */}
        {order.cgstAmount !== undefined && order.cgstAmount !== null && order.cgstAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">
              CGST {order.cgstRate ? `(${order.cgstRate}%)` : ''}
            </span>
            <span className="text-gray-800">{CURRENCY.symbol}{order.cgstAmount.toFixed(2)}</span>
          </div>
        )}

        {/* SGST */}
        {order.sgstAmount !== undefined && order.sgstAmount !== null && order.sgstAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">
              SGST {order.sgstRate ? `(${order.sgstRate}%)` : ''}
            </span>
            <span className="text-gray-800">{CURRENCY.symbol}{order.sgstAmount.toFixed(2)}</span>
          </div>
        )}

        {/* IGST */}
        {order.igstAmount !== undefined && order.igstAmount !== null && order.igstAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">
              IGST {order.igstRate ? `(${order.igstRate}%)` : ''}
            </span>
            <span className="text-gray-800">{CURRENCY.symbol}{order.igstAmount.toFixed(2)}</span>
          </div>
        )}

        {/* Service Charge */}
        {order.serviceChargeAmount !== undefined && order.serviceChargeAmount !== null && order.serviceChargeAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">
              Service Charge {order.serviceChargeRate ? `(${order.serviceChargeRate}%)` : ''}
            </span>
            <span className="text-gray-800">{CURRENCY.symbol}{order.serviceChargeAmount.toFixed(2)}</span>
          </div>
        )}

      </div>

      {/* Total */}
      <div className="mt-4 pt-4 border-t text-right">
        <div className="text-lg font-semibold text-gray-900">
          Total: {CURRENCY.symbol}{order.total.toFixed(2)}
        </div>
      </div>

      <div className="mt-6 text-right">
        <button 
          onClick={handleDownloadPDF}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Download PDF
        </button>
      </div>
    </div>
  );
}
